/**
 * ProcessGraphViewer — read-only diagram sprawy ("Pokaż proces" w `ProcessHistoryTab`).
 *
 * Pokazuje definicję procesu ZE STARTU sprawy (zamrożony snapshot instancji), nie żywą
 * definicję — w układzie narysowanym w kreatorze (pozycje klocków zapisane w snapshocie przy
 * starcie), z nazwami dróg na krawędziach, z wyraźnie zaznaczonym miejscem sprawy:
 *   - sprawa w toku → węzły z `instance.currentNodeIds` ("Tu jest sprawa"),
 *   - sprawa zakończona → etap końcowy `instance.finalNodeId` ("Sprawa zakończona").
 * Gdy definicja procesu zmieniła się od startu sprawy (`definitionChanged`), nad diagramem
 * stoi adnotacja, że to wersja, według której sprawa została wysłana.
 *
 * Mirror `OrgChartCanvas` (React Flow) — NIE `SharedCanvas`/`ProcessGraph` kreatora, który
 * jest zrośnięty z drag&drop z palety. Snapshoty sprzed zapisywania pozycji (albo z procesu
 * bez układu) nie mają współrzędnych — wtedy układ liczy `autoLayout` (dagre), tak samo jak
 * organigram liczy pozycje z drzewa przełożonych.
 */

import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Background, Controls, type Edge, MiniMap, type Node, ReactFlow } from '@xyflow/react';

import { Alert } from '@/components/Alert';
import { autoLayout } from '@/components/process-builder/autoLayout';
import type { ProcessEdgeData, ProcessEdgeType } from '@/components/process-builder/graphTypes';
import { withRouteAppearance } from '@/components/process-builder/stageRoutes';
import type { InstanceDetail, SnapshotEdge, SnapshotNode } from '@/lib/processes/instance-api';
import { caseMarkerOf } from '@/lib/processes/step-tone';

import { type CaseNodeData, caseNodeTypes } from './CaseGraphNodes';

export interface ProcessGraphViewerProps {
  /** Zamrożony graf sprawy (`InstanceDetail.snapshot`). */
  snapshot: { nodes: SnapshotNode[]; edges: SnapshotEdge[] };
  /** Gdzie jest sprawa: bieżące etapy (w toku) albo etap końcowy (zakończona). */
  instance: Pick<InstanceDetail['instance'], 'currentNodeIds' | 'finalNodeId'>;
  /** Definicja procesu zmieniła się od startu sprawy (`InstanceDetail.definitionChanged`). */
  definitionChanged: boolean;
}

const EDGE_OPTIONS = { type: 'smoothstep' as const };

// Zapas wokół grafu przy dopasowaniu widoku — plakietka położenia sprawy wystaje poza klocek.
const FIT_VIEW_OPTIONS = { padding: 0.2 };

interface Point {
  x: number;
  y: number;
}

function readPosition(node: SnapshotNode): Point | null {
  const { positionX: x, positionY: y } = node;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

/**
 * Czy snapshot niesie układ autora: KAŻDY węzeł ma pozycję. Brak pozycji choćby jednego
 * (`null`/pole nieobecne w snapshocie sprzed tej funkcji) → cały graf układa `autoLayout` —
 * mieszanie pozycji autora z wyliczonymi dałoby nakładające się klocki.
 *
 * Wszystkie klocki w jednym punkcie (np. domyślne 0,0 z procesu odpiętego od wzorca bez
 * współrzędnych) to też nie układ autora: nałożyłyby się na siebie, więc traktujemy jak brak.
 */
export function hasStoredLayout(nodes: readonly SnapshotNode[]): boolean {
  if (nodes.length === 0) return false;
  const points: Point[] = [];
  for (const node of nodes) {
    const point = readPosition(node);
    if (!point) return false;
    points.push(point);
  }
  const first = points[0];
  if (points.length > 1 && first && points.every((p) => p.x === first.x && p.y === first.y)) {
    return false;
  }
  return true;
}

/**
 * Transformacja snapshotu → węzły React Flow. Pozycje ze snapshotu, gdy graf ma komplet
 * pozycji (`useStoredLayout` = `hasStoredLayout`, liczone raz przez wołającego), inaczej (0,0) —
 * wtedy wołający układa je `autoLayout`. Czysta funkcja — testowalna bez renderu React Flow
 * (jsdom go nie lubi — patrz `ProcessGraphViewer.test.tsx`).
 */
export function snapshotNodesToFlowNodes(
  nodes: readonly SnapshotNode[],
  currentNodeIds: ReadonlySet<string>,
  finalNodeId: string | null,
  useStoredLayout: boolean = hasStoredLayout(nodes),
): Node<CaseNodeData>[] {
  return nodes.map((n) => {
    const marker = caseMarkerOf(n.id, currentNodeIds, finalNodeId);
    return {
      id: n.id,
      type: n.nodeType,
      position: (useStoredLayout ? readPosition(n) : null) ?? { x: 0, y: 0 },
      data: {
        label: n.label ?? undefined,
        approverConfig: n.approverConfig,
        // Węzły kreatora podświetlają się po `isCurrent`; etap końcowy zakończonej sprawy
        // dostaje to samo podświetlenie, a rozróżnia go dopiero plakietka (`caseMarker`).
        isCurrent: marker !== null,
        caseMarker: marker ?? undefined,
      },
    };
  });
}

/**
 * Transformacja krawędzi snapshotu → krawędzie React Flow. Wygląd drogi (nazwa na krawędzi,
 * ★ przy domyślnej, linia przerywana dla warunkowej) liczy ten sam `withRouteAppearance`, który
 * rysuje drogi w kreatorze — diagram sprawy wygląda jak schemat autora; droga bez nazwy nie
 * dostaje etykiety. Czysta funkcja — patrz uwaga wyżej.
 */
export function snapshotEdgesToFlowEdges(edges: readonly SnapshotEdge[]): Edge<ProcessEdgeData>[] {
  // Defensywnie pomijamy krawędzie bez targetu (spójnie z fail-safe w addressee-resolution.ts) —
  // no-op dla poprawnych migawek, chroni podgląd starszych instancji przed osieroconą krawędzią.
  return edges
    .filter((e) => Boolean(e.targetNodeId))
    .map((e) =>
      withRouteAppearance({
        id: e.id,
        source: e.sourceNodeId,
        target: e.targetNodeId,
        data: {
          edgeType: e.edgeType as ProcessEdgeType,
          label: e.label?.trim() ? e.label : undefined,
          isDefault: e.isDefault === true,
        },
      }),
    );
}

export function ProcessGraphViewer({
  snapshot,
  instance,
  definitionChanged,
}: ProcessGraphViewerProps): ReactElement {
  const { t } = useTranslation();
  const { currentNodeIds, finalNodeId } = instance;

  const { flowNodes, flowEdges } = useMemo(() => {
    const useStoredLayout = hasStoredLayout(snapshot.nodes);
    const rawNodes = snapshotNodesToFlowNodes(
      snapshot.nodes,
      new Set(currentNodeIds ?? []),
      finalNodeId,
      useStoredLayout,
    );
    const rawEdges = snapshotEdgesToFlowEdges(snapshot.edges);
    return {
      // Układ liczony tu zapada przed pierwszym pomiarem klocków, a ich szerokość bywa zmienna
      // (pigułka etapu końcowego zależy od nazwy) — `anchor: 'center'` stawia każdy klocek
      // ŚRODKIEM na osi z dagre, więc strzałki do górnego środka biegną pionowo.
      flowNodes: useStoredLayout
        ? rawNodes
        : autoLayout(rawNodes, rawEdges, { direction: 'TB', anchor: 'center' }),
      flowEdges: rawEdges,
    };
  }, [snapshot, currentNodeIds, finalNodeId]);

  return (
    <div className="flex h-full w-full flex-col gap-2" data-testid="process-graph-viewer">
      {definitionChanged && (
        <Alert tone="info" className="shrink-0" data-testid="process-graph-definition-changed">
          {t('tenant.processes.flow.definitionChanged')}
        </Alert>
      )}
      <div className="min-h-0 flex-1">
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={caseNodeTypes}
          defaultEdgeOptions={EDGE_OPTIONS}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          fitView
          fitViewOptions={FIT_VIEW_OPTIONS}
          minZoom={0.2}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
    </div>
  );
}
